import type {ChatMessage, ChatMessagePart, MessagesResponse} from '@Components/ai-chat-window/ai-chat-window.types';
import {MessageRole} from '@Components/ai-chat-window/ai-chat-window.types';
import type {PromptAttachment} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {PromptAttachmentType} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';

export const getAiChatCompletedStateForInProgressChat = async (chatId: string, messageSessionUid: string): Promise<MessagesResponse> => {
  return (await window.PMW.pollingAjaxCallAsync(
    async (): Promise<void> => {},
    async (): Promise<MessagesResponse> => {
      return window.PMW.readLocal('ai/getAiChatCompletedState', {
        chatId: chatId,
        messageSessionUid: messageSessionUid,
      });
    }
  )) as MessagesResponse;
};

export const groupMessagesIntoConversations = (messages: ChatMessage[]): ChatMessage[][] => {
  const conversations: ChatMessage[][] = [];
  let currentConversation: ChatMessage[] = [];

  messages.forEach((message) => {
    if (message.role === MessageRole.USER) {
      if (currentConversation.length > 0) {
        conversations.push(currentConversation);
      }
      currentConversation = [message];
    } else {
      currentConversation.push(message);
    }
  });

  if (currentConversation.length > 0) {
    conversations.push(currentConversation);
  }

  return conversations;
};

export const findLastUserMessage = (messages: ChatMessage[]): ChatMessage | undefined => {
  return [...messages].reverse().find((msg) => msg.role === MessageRole.USER);
};

export const convertAttachmentsToUserRequestParts = (attachments: PromptAttachment[]): ChatMessagePart[] => {
  const userRequestParts: ChatMessagePart[] = [];

  attachments.forEach((attachment) => {
    switch (attachment.type) {
      case PromptAttachmentType.IMAGE:
        userRequestParts.push({
          type: 'user_image',
          hashedFilename: attachment.hashedFilename,
        });
        break;
      case PromptAttachmentType.DESIGN:
        userRequestParts.push({
          type: 'design',
          hashedId: attachment.designId,
        });
        break;
    }
  });

  return userRequestParts;
};

export const createUserRequestParts = (prompt: string, attachments?: PromptAttachment[]): ChatMessagePart[] => {
  const attachmentsUserRequestParts = convertAttachmentsToUserRequestParts(attachments ?? []);
  return [{type: 'text', text: prompt}, ...attachmentsUserRequestParts];
};

export const scrollToBottom = (container: HTMLDivElement | null): void => {
  if (!container) {
    return;
  }
  container.scrollTo({top: container.scrollHeight, behavior: 'smooth'});
};

export const adjustLastConversationSpacing = (messagesContainer: HTMLDivElement | null): void => {
  if (!messagesContainer) {
    return;
  }

  const conversations = messagesContainer.querySelectorAll('[data-conversation]');
  if (conversations.length === 0) {
    return;
  }

  conversations.forEach((conversation) => {
    (conversation as HTMLElement).style.minHeight = '';
  });

  const lastConversation = conversations[conversations.length - 1] as HTMLElement;
  const containerHeight = messagesContainer.clientHeight;

  lastConversation.style.minHeight = `${containerHeight}px`;
  scrollToBottom(messagesContainer);
};
